import React, { useState } from 'react';
import { TourInfo } from './types';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Plus, Map } from 'lucide-react';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { forceUpdateTourCoordinates } from "@/lib/quotation-storage";
import { normalizeCoordinates } from "@/lib/coordinates-utils";
import { TourForm } from './TourForm';
import { format } from 'date-fns';
import { PageSelectionModal } from './PageSelectionModal';

interface TourManagerProps {
  pageId: string;
  tours: TourInfo[];
  updateTours: (tours: TourInfo[]) => void;
  collapsedItems: Record<string, boolean>;
  onToggleItem: (id: string) => void;
  hideAddButton?: boolean;
  hideTitle?: boolean;
  allPages?: any[]; // All pages in the quotation
  removeTourFromPage?: (pageId: string, tourId: string) => void;
  addTourToPage?: (pageId: string, tour: TourInfo) => void;
  moveTourToPage?: (sourcePageId: string, tourId: string, targetPageId: string) => void;
}

export function TourManager({ 
  pageId, 
  tours, 
  updateTours,
  collapsedItems,
  onToggleItem,
  hideAddButton = false,
  hideTitle = false,
  allPages = [],
  removeTourFromPage,
  addTourToPage,
  moveTourToPage
}: TourManagerProps) {
  // Adicionar log para debug
  console.log('TourManager - allPages:', allPages);
  
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isProcessingTourDescription, setIsProcessingTourDescription] = useState(false);
  const [isProcessingTourNameToDescription, setIsProcessingTourNameToDescription] = useState<Record<string, boolean>>({});
  // State for page selection modal
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);
  const [tourToMove, setTourToMove] = useState<string | null>(null);

  // Escutar eventos de expansão de itens para colapsar os passeios quando outro item for expandido
  React.useEffect(() => {
    const handleItemExpanded = (event: CustomEvent) => {
      const { itemType, itemId, pageId: eventPageId } = event.detail;
      
      // Se o evento for de outra página ou for para um passeio, ignorar
      if (eventPageId !== pageId || itemType === 'tour') {
        return;
      }
      
      // Just notify parent component instead of trying to auto-collapse specific items
      onToggleItem('');
    };
    
    // Adicionar o event listener
    document.addEventListener('item-expanded', handleItemExpanded as EventListener);
    
    // Remover ao desmontar o componente
    return () => {
      document.removeEventListener('item-expanded', handleItemExpanded as EventListener);
    };
  }, [pageId, onToggleItem]);

  // Função para adicionar novo passeio
  const addTour = () => {
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const newTour: TourInfo = {
      id: `tour-${Date.now()}`,
      name: '',
      date: formattedDate,
      time: '',
      duration: '',
      location: '',
      description: '',
      price: undefined,
      currency: 'BRL',
      images: [],
      globalOrder: tours.length + 1  // Increment by 1 to place at the end
    };
    
    updateTours([...(tours || []), newTour]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(newTour.id);
    }, 0);
  };

  // Função para remover um passeio
  const removeTour = (tourId: string) => {
    const updatedTours = tours.filter(tour => tour.id !== tourId);
    updateTours(updatedTours);
  };

  // Função para atualizar um campo específico de um passeio
  const updateTour = (tourId: string, field: keyof TourInfo, value: any) => {
    let safeValue;
    
    // Special handling for coordinates to ensure they're always properly formatted numbers
    if (field === 'coordinates' && value) {
      try {
        // Garantir que lat e lng existam
        if (value.lat === undefined || value.lng === undefined) {
          console.error("[TourManager] Coordinates missing lat or lng properties:", value);
          safeValue = null;
        } else {
          // Converter explicitamente para números
          const latStr = String(value.lat).replace(',', '.');
          const lngStr = String(value.lng).replace(',', '.');
          
          const lat = Number(latStr);
          const lng = Number(lngStr);
          
          // Validar se são números
          if (isNaN(lat) || isNaN(lng)) {
            console.error("[TourManager] Invalid coordinate values after conversion:", {
              original: value,
              latStr, lngStr,
              lat, lng
            });
            safeValue = null;
          } else {
            // Criar objeto com valores numéricos explícitos
      safeValue = {
              lat: lat,
              lng: lng
            };
          }
        }
      } catch (err) {
        console.error("[TourManager] Error processing coordinates:", err);
        safeValue = null;
      }
      
      // Se não conseguimos extrair coordenadas válidas, registrar erro
      if (!safeValue) {
        console.error("[TourManager] Failed to process coordinates:", value);
      } else {
        console.log("[TourManager] Processed coordinates successfully:", {
        original: value,
        normalized: safeValue,
          latType: typeof safeValue.lat,
          lngType: typeof safeValue.lng,
          latValue: safeValue.lat,
          lngValue: safeValue.lng
        });
      }
    } 
    else {
      // For all other fields, use regular deep copy
      safeValue = typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value;
    }
    
    const updatedTours = tours.map((tour: TourInfo) => {
      if (tour.id === tourId) {
        const updated = { ...tour, [field]: safeValue };
        
        // Extra debugging for coordinate updates
        if (field === 'coordinates') {
          console.log('[TourManager] Updated tour with new coordinates:', {
            tour: tour.id,
            name: tour.name,
            oldCoordinates: tour.coordinates,
            newCoordinates: safeValue,
            fullObject: updated
          });
          
          // Ensure coordinates are properly saved
          setTimeout(() => {
            forceUpdateTourCoordinates(tourId, safeValue);
          }, 300);
        }
        
        return updated;
      }
      return tour;
    });
    
    console.log(`[TourManager] Updated field ${String(field)}:`, {
      tourId,
      fieldName: field,
      newValue: safeValue,
      valueType: typeof safeValue,
      isObject: typeof safeValue === 'object',
      hasCoordinates: field === 'coordinates'
    });
    
    console.log('[TourManager] Calling updateTours with:', {
      updatedToursCount: updatedTours.length,
      hasUpdatedTour: updatedTours.some(t => t.id === tourId),
      updatedTour: updatedTours.find(t => t.id === tourId),
    });
    
    updateTours(updatedTours);
  };

  // Função para atualizar múltiplos campos de um passeio de uma vez
  const batchUpdateTour = (tourId: string, updates: Partial<TourInfo>) => {
    console.log('[TourManager] Batch updating tour:', {
      tourId,
      updates,
      hasCoordinates: !!updates.coordinates,
      coordinatesDetail: updates.coordinates ? {
        lat: updates.coordinates.lat,
        lng: updates.coordinates.lng,
        latType: typeof updates.coordinates.lat,
        lngType: typeof updates.coordinates.lng
      } : 'none'
    });
    
    // Create a copy of updates for safe modification
    const safeUpdates = { ...updates };
    
    // Ensure coordinates are normalized if present
    if (updates.coordinates) {
      try {
        // Extrair e validar valores das coordenadas
        const coordInput = updates.coordinates;
        
        // Garantir que lat e lng existam
        if (coordInput.lat === undefined || coordInput.lng === undefined) {
          console.error("[TourManager] Batch update coordinates missing lat/lng:", coordInput);
          delete safeUpdates.coordinates;
        } else {
          // Converter para string e depois para número para garantir consistência
          const latStr = String(coordInput.lat).replace(',', '.');
          const lngStr = String(coordInput.lng).replace(',', '.');
          
          const lat = Number(latStr);
          const lng = Number(lngStr);
          
          // Validar se são números válidos
          if (isNaN(lat) || isNaN(lng)) {
            console.error("[TourManager] Invalid coordinates in batch update:", {
              original: coordInput,
              latStr, lngStr,
              lat, lng
            });
            delete safeUpdates.coordinates;
          } else {
            // Atualizar com valores numéricos garantidos
            safeUpdates.coordinates = {
              lat: lat,
              lng: lng
            };
            
            console.log('[TourManager] Coordinates normalized in batch update:', {
              original: coordInput,
              normalized: safeUpdates.coordinates,
              latType: typeof lat,
              lngType: typeof lng,
              latValue: lat,
              lngValue: lng
            });
          }
        }
      } catch (err) {
        console.error('[TourManager] Error processing coordinates in batch update:', err);
        delete safeUpdates.coordinates;
      }
    }
    
    const updatedTours = tours.map((tour: TourInfo) => {
      if (tour.id === tourId) {
        // Create a deep copy of the tour with updates applied
        const updated = {
          ...tour,
          ...safeUpdates
        };
        
        // For debugging coordinate updates
        if (updates.coordinates) {
          console.log('[TourManager] Updated tour with new coordinates in batch:', {
          tourId,
          name: tour.name,
            oldCoordinates: tour.coordinates,
            newCoordinates: safeUpdates.coordinates,
            fullObject: updated
          });
          
          // Ensure coordinates are properly saved to localStorage
          if (safeUpdates.coordinates) {
            setTimeout(() => {
              forceUpdateTourCoordinates(tourId, safeUpdates.coordinates!);
            }, 300);
          }
        }
        
        return updated;
      }
      return tour;
    });
    
    console.log('[TourManager] Calling updateTours with:', {
      updatedToursCount: updatedTours.length,
      hasUpdatedTour: updatedTours.some(t => t.id === tourId),
      updatedTour: updatedTours.find(t => t.id === tourId),
    });
    
    updateTours(updatedTours);
  };

  // Função para alternar o estado de colapso de um passeio
  const toggleTourCollapse = (tourId: string) => {
    // If empty ID, it's just a signal to collapse all - forward to parent component
    if (!tourId) {
      onToggleItem('');
      return;
    }
    
    // Se o item atual está colapsado (fechado), vamos abri-lo e fechar todos os outros
    if (collapsedItems[tourId]) {
      // Abrir apenas o item atual
      onToggleItem(tourId);
      
      // Disparar um evento para notificar outros componentes que um passeio foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'tour',
          itemId: tourId,
          pageId: pageId
        }
      }));
    } else {
      // Se está aberto, apenas fechamos
      onToggleItem(tourId);
    }
  };

  // Função para duplicar um passeio
  const duplicateTour = (sourcePageId: string, tourId: string) => {
    const tourToDuplicate = tours.find(
      (tour: TourInfo) => tour.id === tourId
    );
    
    if (!tourToDuplicate) return;
    
    // Deep clone the tour object
    const duplicatedTour: TourInfo = JSON.parse(JSON.stringify(tourToDuplicate));
    
    // Update properties for the duplicate
    duplicatedTour.id = `tour-${Date.now()}`;
    duplicatedTour.name = duplicatedTour.name ? `${duplicatedTour.name} (Cópia)` : '';
    duplicatedTour.globalOrder = tours.length + 1; // Place at the end
    
    // Add the duplicated tour to the list
    updateTours([...tours, duplicatedTour]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(duplicatedTour.id);
    }, 0);
    
    toast({
      title: "Passeio duplicado",
      description: "O passeio foi duplicado com sucesso.",
    });
  };
  
  // Function to initiate the tour move process
  const handleInitiateTourMove = (tourId: string) => {
    // Calculate available pages (excluding current page)
    const availablePages = allPages.filter(page => page.id !== pageId);
    
    if (availablePages.length === 0) {
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover o passeio.",
      });
      return;
    }
    
    // Set the tour to move and open the page selection modal
    setTourToMove(tourId);
    setIsPageSelectionOpen(true);
  };
  
  // Handle page selection for tour movement
  const handleMoveToPage = (targetPageId: string) => {
    if (!tourToMove) return;
    
    // Find the tour to move
    const tour = tours.find(t => t.id === tourToMove);
    if (!tour) return;
    
    // Close the modal
    setIsPageSelectionOpen(false);
    
    // Use the moveTourToPage function directly with all required parameters
    if (moveTourToPage) {
      // Call the function with source page ID, tour ID, and target page ID
      moveTourToPage(pageId, tourToMove, targetPageId);
      
      toast({
        title: "Passeio movido",
        description: `O passeio foi movido para a página selecionada.`,
      });
    } else {
      // Fallback: just show a toast that we can't move the tour
      toast({
        title: "Funcionalidade em desenvolvimento",
        description: "A função de mover para outra página será implementada em breve.",
      });
    }
    
    // Clear the tour to move
    setTourToMove(null);
  };

  return (
    <div className="space-y-4">
      {!hideTitle && (
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium">Passeios</h3>
          {!hideAddButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
              onClick={addTour}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Passeio
            </Button>
          )}
        </div>
      )}

      {hideTitle && !hideAddButton && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
            onClick={addTour}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Passeio
          </Button>
        </div>
      )}

      {(!tours || tours.length === 0) ? (
        <div className="p-6 border border-dashed rounded-md text-center">
          <Map className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Você ainda não adicionou nenhum passeio a esta cotação.</p>
          {!hideAddButton && (
            <Button 
              variant="outline"
              onClick={addTour}
              className="bg-white hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Passeio
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {tours.map((tour: TourInfo, idx: number) => (
            <TourForm
              key={tour.id}
              tour={tour}
              index={idx}
              pageId={pageId}
              updateTour={(pageId, tourId, field, value) => {
                // For single field updates
                updateTour(tourId, field, value);
              }}
              batchUpdateTour={batchUpdateTour}
              deleteTour={(pageId, tourId) => {
                removeTour(tourId);
              }}
              duplicateTour={duplicateTour}
              moveTourToPage={handleInitiateTourMove}
              isCollapsed={collapsedItems[tour.id] !== false}
              onToggleCollapse={() => toggleTourCollapse(tour.id)}
            />
          ))}
        </div>
      )}
      
      {/* Page Selection Modal */}
      {isPageSelectionOpen && tourToMove && (
        <PageSelectionModal
          isOpen={isPageSelectionOpen}
          onClose={() => {
            setIsPageSelectionOpen(false);
            setTourToMove(null);
          }}
          pages={allPages}
          currentPageId={pageId}
          onSelectPage={handleMoveToPage}
          title="Mover passeio para outra página"
        />
      )}
    </div>
  );
} 